// Entry router. index.html loads THIS file, and its job is to decide — before importing anything
// expensive — whether this page is the Designer or the cross-device preview receiver.
//
// ES imports all resolve before any module body runs, so routing inside the Designer's own entry meant
// the whole store → utils → three.js graph loaded first: a wasted ~3.4 MB in production, and in dev a
// phone fetching the entire Designer graph over the tunnel before any routing decision.
//
// So: static imports here are react/react-dom plus the pure route matcher, and each branch loads
// dynamically. Nothing added here may pull in the store, the sync layer, settings, or components/.

import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { matchRemotePreviewRoute } from './preview/webrtc/remote-preview-routes';

const RemotePreviewReceiver = React.lazy(() => import('./components/dom/preview/RemotePreviewReceiver/RemotePreviewReceiver'));

/**
 * The healthy-path screen: a spinner, and nothing else. Getting a preview onto a phone used to walk
 * through four screens the user could do nothing about; they are collapsed into splash → spinner → JUTE.
 *
 * SVG `animateTransform`, not CSS: this file must stay dependency-free, so there is no stylesheet to
 * declare a keyframe in. `#5276F7` is `COLORS.nebula`, hardcoded for the same reason.
 */
function ReceiverSpinner() {
    return <div style={{
        display: 'flex',
        width: '100vw',
        height: '100dvh',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#F6F6F9',
    }}>
        <svg width="40" height="40" viewBox="0 0 40 40" role="progressbar" aria-label="Loading preview">
            <circle cx="20" cy="20" r="16" fill="none" stroke="#DDE3F5" strokeWidth="4" />
            <path d="M20 4 a16 16 0 0 1 16 16" fill="none" stroke="#5276F7" strokeWidth="4" strokeLinecap="round">
                <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 20 20"
                    to="360 20 20"
                    dur="0.8s"
                    repeatCount="indefinite"
                />
            </path>
        </svg>
    </div>;
}

/**
 * Diagnostic screen, shown ONLY on a real failure. Dependency-free for the same reason as above: it
 * renders on a phone with none of the app booted, and with no console to open.
 */
function ReceiverBootScreen({ message }: { message: string }) {
    const facts = [
        `path: ${location.pathname}`,
        `pid: ${new URLSearchParams(location.search).get('pid') ?? '(missing)'}`,
        `secure context: ${window.isSecureContext ? 'yes' : 'NO — service workers are unavailable'}`,
        `service workers: ${'serviceWorker' in navigator ? 'supported' : 'NOT supported'}`
    ];
    return <div style={{
        display: 'flex',
        width: '100vw',
        height: '100dvh',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '24px',
        boxSizing: 'border-box',
        backgroundColor: '#F6F6F9',
        fontFamily: 'Lato, sans-serif',
    }}>
        <div style={{
            maxWidth: '360px',
            padding: '32px 24px',
            borderRadius: '11px',
            backgroundColor: 'white',
            color: '#073158',
            boxShadow: '0 2px 4px 0 rgba(0,0,0,0.16)',
            textAlign: 'center',
        }}>
            <h1 style={{ margin: '0 0 16px', fontSize: '20px' }}>Live preview</h1>
            <p style={{ margin: '0 0 16px', fontSize: '14px', lineHeight: 1.4 }}>{message}</p>
            <div style={{ fontSize: '11px', color: '#9b9b9b', textAlign: 'left', lineHeight: 1.6, wordBreak: 'break-all' }}>
                {facts.map((f) => <div key={f}><code>{f}</code></div>)}
            </div>
        </div>
    </div>;
}

/** Plain boundary rather than Sentry's: importing @sentry/react here would defeat the point of this
 *  file. Sentry still initialises on the Designer path, inside designer-entry. */
class ReceiverErrorBoundary extends React.Component<{ children: React.ReactNode }, { error: Error | null }> {
    state: { error: Error | null } = { error: null };

    static getDerivedStateFromError(error: Error) {
        return { error };
    }

    componentDidCatch(error: Error) {
        console.error('[webrtc-recv] receiver crashed', error);
    }

    render() {
        if (this.state.error) {
            return <ReceiverBootScreen message={`The preview failed to start: ${this.state.error.message}`} />;
        }
        return this.props.children;
    }
}

const route = matchRemotePreviewRoute(location.pathname);

if (route === 'app') {
    // The Designer, unchanged — everything that used to be in this file lives there now.
    void import('./designer-entry');
} else {
    const container = document.getElementById('root') || document.createElement('div');
    const root = createRoot(container);

    if (route === 'receiver') {
        console.log('[webrtc-recv] remote-preview route — rendering the receiver instead of Designer');
        root.render(
            // Deliberately VISIBLE rather than null. index.html ships a static splash inside #root, so a null
            // fallback made three states identical on a phone with no console: JS never ran, the chunk is
            // still downloading, or the chunk failed.
            <ReceiverErrorBoundary>
                <React.Suspense fallback={<ReceiverSpinner />}>
                    <RemotePreviewReceiver />
                </React.Suspense>
            </ReceiverErrorBoundary>
        );
    } else {
        // 'sw-miss' — the worker didn't answer a path only it should, and the SPA fallback returned this
        // shell with HTTP 200 instead. Say so, rather than booting the editor in someone's preview frame.
        console.error(
            `[webrtc-recv] ${location.pathname} should have been served by the preview service worker. ` +
                'It was served by the SPA fallback instead, so the worker is not controlling its scope.'
        );
        root.render(
            <ReceiverBootScreen
                message="The preview service worker isn’t serving this session, so there is nothing to run. Scan the QR code in Designer again, or reload to re-register the service worker."
            />
        );
    }
}
